/* Meeras Organics — scroll-scrubbed product film engine.
   Canvas image-sequence scrubber: scroll position drives frame index.
   Frame URLs are real theme assets (window.MEERAS_FRAME_URLS, generated
   by Liquid in the section that includes this script) — not embedded
   data-URIs — so the browser can cache and parallel-fetch them normally.
   Only a sliding window of decoded ImageBitmaps is kept around the
   current frame, to keep memory sane on mobile. */

const canvas = document.getElementById("meeras-film");
const ctx = canvas.getContext("2d");
const track = document.getElementById("meeras-track");
const loader = document.getElementById("meeras-loader");
const loadbar = document.getElementById("meeras-loadbar");
const scrollCue = document.getElementById("meeras-scroll-cue");
const captions = [...document.querySelectorAll(".meeras-caption")];

const KEEP = 100;
const AHEAD = 24;

const state = {
  blobs: [],
  bitmaps: new Map(),
  count: 0,
  current: -1,
  target: 0,
  smooth: 0,
  dir: 1,
  ready: false,
  decoding: new Set(),
};

function frameURL(i) {
  return (window.MEERAS_FRAME_URLS || [])[i];
}

async function fetchBlob(i) {
  if (state.blobs[i]) return state.blobs[i];
  const url = frameURL(i);
  if (!url) return null;
  const res = await fetch(url);
  state.blobs[i] = await res.blob();
  return state.blobs[i];
}

async function decode(i) {
  if (state.bitmaps.has(i) || state.decoding.has(i) || !state.blobs[i]) return;
  state.decoding.add(i);
  try {
    const bmp = await createImageBitmap(state.blobs[i]);
    state.bitmaps.set(i, bmp);
  } catch { /* transient — retried next tick */ }
  state.decoding.delete(i);
}

function manageWindow(center) {
  for (let d = 0; d <= AHEAD; d++) {
    const fwd = center + d * state.dir;
    const back = center - Math.min(d, 6) * state.dir;
    if (fwd >= 0 && fwd < state.count) decode(fwd);
    if (back >= 0 && back < state.count) decode(back);
  }
  if (state.bitmaps.size > KEEP * 2) {
    for (const [idx, bmp] of state.bitmaps) {
      if (Math.abs(idx - center) > KEEP) {
        bmp.close();
        state.bitmaps.delete(idx);
      }
    }
  }
}

async function preload() {
  const { count } = state;
  const EAGER = Math.min(Math.ceil(count * 0.12), 40);

  let done = 0;
  await Promise.all(
    Array.from({ length: EAGER }, (_, i) =>
      fetchBlob(i).then(() => {
        done++;
        loadbar.style.width = `${(done / EAGER) * 100}%`;
      })
    )
  );
  await decode(0);
  state.ready = true;
  loader.classList.add("meeras-done");

  let next = EAGER;
  await Promise.all(
    Array.from({ length: 5 }, async () => {
      while (next < count) {
        const i = next++;
        try { await fetchBlob(i); } catch { /* refetched on demand */ }
      }
    })
  );
}

function resize() {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.round(canvas.clientWidth * dpr);
  canvas.height = Math.round(canvas.clientHeight * dpr);
  state.current = -1;
}

function nearestDecoded(i) {
  if (state.bitmaps.has(i)) return i;
  for (let d = 1; d < state.count; d++) {
    if (state.bitmaps.has(i - d)) return i - d;
    if (state.bitmaps.has(i + d)) return i + d;
  }
  return -1;
}

function drawFrame(i) {
  const j = nearestDecoded(i);
  if (j < 0) return;
  const bmp = state.bitmaps.get(j);
  const cw = canvas.width, ch = canvas.height;
  ctx.fillStyle = "#0a0908";
  ctx.fillRect(0, 0, cw, ch);
  const s = Math.min(cw / bmp.width, ch / bmp.height) * 1.04;
  const w = bmp.width * s, h = bmp.height * s;
  ctx.drawImage(bmp, (cw - w) / 2, (ch - h) / 2, w, h);
  state.current = j;
}

function progress() {
  const max = track.offsetHeight - window.innerHeight;
  return max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
}

function transformBase(el) {
  if (el.classList.contains("meeras-cap-center")) return "translate(-50%, -50%)";
  if (el.classList.contains("meeras-cap-bottom")) return "translateX(-50%)";
  return "translateY(-50%)";
}

function updateCaptions(p) {
  for (const el of captions) {
    const tIn = +el.dataset.in, tHold = +el.dataset.hold, tOut = +el.dataset.out;
    const rise = Math.max((tHold - tIn) * 0.4, 0.008);
    const fall = Math.max((tOut - tHold) * 0.6, 0.008);
    let o = 0;
    if (p >= tIn && p <= tOut) {
      o = Math.min((p - tIn) / rise, 1) * Math.min((tOut - p) / fall, 1);
      o = Math.min(Math.max(o, 0), 1);
    }
    el.style.opacity = o.toFixed(3);
    const drift = (p - tHold) * -40;
    el.style.transform = `${transformBase(el)} translateY(${drift.toFixed(1)}px)`;
  }
  if (scrollCue) scrollCue.style.opacity = p < 0.015 ? 1 : 0;
}

let lastT = performance.now();
function tick(now) {
  const dt = Math.min((now - lastT) / 1000, 0.5) || 0.016;
  lastT = now;
  if (state.ready) {
    const p = progress();
    const prevTarget = state.target;
    state.target = p * (state.count - 1);
    if (state.target !== prevTarget) state.dir = state.target >= prevTarget ? 1 : -1;
    const k = 1 - Math.exp(-dt * 14);
    state.smooth += (state.target - state.smooth) * k;
    if (Math.abs(state.target - state.smooth) < 0.5) state.smooth = state.target;
    const i = Math.round(state.smooth);
    manageWindow(i);
    const t = Math.round(state.target);
    if (t !== i) {
      for (let d = -6; d <= 6; d++) {
        const ti = t + d;
        if (ti >= 0 && ti < state.count) decode(ti);
      }
    }
    if (i !== state.current) drawFrame(i);
    updateCaptions(p);
  }
  requestAnimationFrame(tick);
}

function devPlaceholder(msg) {
  loader.classList.add("meeras-done");
  const draw = () => {
    const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
    g.addColorStop(0, "#141210");
    g.addColorStop(1, "#0a0908");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(201,160,80,0.85)";
    ctx.font = `${16 * (window.devicePixelRatio || 1)}px -apple-system, sans-serif`;
    ctx.textAlign = "center";
    ctx.fillText(msg, canvas.width / 2, canvas.height / 2);
  };
  draw();
  window.addEventListener("resize", () => { resize(); draw(); });
}

window.addEventListener("resize", resize);
resize();

if (window.MEERAS_FRAME_URLS && window.MEERAS_FRAME_URLS.length) {
  state.count = window.MEERAS_FRAME_URLS.length;
  state.blobs = new Array(state.count).fill(null);
  requestAnimationFrame(tick);
  preload();
} else {
  devPlaceholder("Meeras film frames not found");
}
